import { Edge, MarkerType, Node } from "@xyflow/react";
import { CallFlowActionRequest } from "@/model/interface/api/CallFlowActionRequest";
import { CallFlow, CallFlowNodeData } from "@/model/interface/CallFlow";
import { CallFlowAction } from "@/model/interface/CallFlowAction";
import { CallFlowActionType } from "@/model/interface/CallFlowActionType";
import { NODE_EDGES, NODE_LABELS, edgeKeysFor, edgeLabelFor, isTerminalNodeType } from "@/util/callFlowNodes";

export function serializeCanvas(nodes: Node[], edges: Edge[]): string {
  const comparableNodes = nodes.map((node) => ({
    id: node.id,
    type: node.type,
    data: node.data,
  }));

  const comparableEdges = edges.map((edge) => ({
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle ?? null,
    targetHandle: edge.targetHandle ?? null,
  }));

  return JSON.stringify({ nodes: comparableNodes, edges: comparableEdges });
}

export function convertNodesToActions(nodes: Node[], edges: Edge[]): CallFlowActionRequest[] {
  const actionNodes = nodes.filter((node) => node.type === "action" || node.type === "end");
  const nodeById = new Map(actionNodes.map((node) => [node.id, node]));

  let rootId: string | undefined;
  const outgoing = new Map<string, Map<string, string>>();
  edges.forEach((edge) => {
    if (edge.source === "start") {
      rootId = rootId ?? edge.target;
      return;
    }
    const sourceNode = nodeById.get(edge.source);
    if (!sourceNode) {
      return;
    }
    const actionType = (sourceNode.data as any).actionType as CallFlowActionType;
    const edgeKey = edge.sourceHandle ?? edgeKeysFor(actionType, sourceNode.data)[0];
    if (!edgeKey) {
      return;
    }
    const sourceEdges = outgoing.get(edge.source) ?? new Map<string, string>();
    sourceEdges.set(edgeKey, edge.target);
    outgoing.set(edge.source, sourceEdges);
  });

  if (!rootId || !nodeById.has(rootId)) {
    return [];
  }

  const orderedIds: string[] = [];
  const visited = new Set<string>();
  const queue: string[] = [rootId];
  while (queue.length > 0) {
    const currentId = queue.shift()!;
    if (visited.has(currentId) || !nodeById.has(currentId)) {
      continue;
    }
    visited.add(currentId);
    orderedIds.push(currentId);
    outgoing.get(currentId)?.forEach((targetId) => {
      queue.push(targetId);
    });
  }

  return orderedIds.map((nodeId, index) => {
    const node = nodeById.get(nodeId)!;
    const data = node.data as any;
    const metadata = buildActionMetadata(data);
    const branches: Record<string, string> = {};
    outgoing.get(nodeId)?.forEach((targetId, edgeKey) => {
      if (visited.has(targetId)) {
        branches[edgeKey] = targetId;
      }
    });

    return {
      clientId: nodeId,
      actionType: data.actionType,
      sequence: index,
      targetMembership: data.targetMembershipId ? { id: data.targetMembershipId } : undefined,
      targetPhoneNumber: data.targetPhoneNumber,
      voicemailGreeting: data.voicemailGreeting,
      message: data.message,
      timeoutSeconds: data.timeoutSeconds,
      metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
      branches,
    };
  });
}

export function buildCanvasFromFlow(callFlow: CallFlow): { nodes: Node[]; edges: Edge[] } {
  const actions = [...(callFlow.actions ?? [])].sort((first, second) => first.sequence - second.sequence);
  const storedNodes = (callFlow.flowData?.nodes as Node[]) ?? [];

  if (actions.length === 0) {
    return {
      nodes: storedNodes,
      edges: (callFlow.flowData?.edges as Edge[]) ?? [],
    };
  }

  const storedNodeById = new Map(storedNodes.map((node) => [node.id, node]));
  const canvasIdByServerId = new Map(
    actions.filter((action) => action.id).map((action) => [action.id!, action.clientId ?? action.id!]),
  );
  const hasStoredBranches = actions.some((action) => action.branches != null);

  const nodes: Node[] = [
    {
      id: "start",
      type: "start",
      position: storedNodeById.get("start")?.position ?? { x: 250, y: 50 },
      data: {},
    },
  ];
  const edges: Edge[] = [];

  actions.forEach((action, index) => {
    const canvasId = action.clientId ?? action.id ?? `action-${index}`;
    const storedData = (storedNodeById.get(canvasId)?.data ?? {}) as Record<string, unknown>;

    const serverFields: Record<string, unknown> = {
      targetMembershipId: action.targetMembership?.id != null ? String(action.targetMembership.id) : undefined,
      targetPhoneNumber: action.targetPhoneNumber,
      voicemailGreeting: action.voicemailGreeting,
      message: action.message,
      timeoutSeconds: action.timeoutSeconds,
      ...(action.metadata ?? {}),
    };
    const definedServerFields = Object.fromEntries(
      Object.entries(serverFields).filter(([, value]) => value !== undefined && value !== null),
    );

    nodes.push({
      id: canvasId,
      type: isTerminalNodeType(action.actionType) ? "end" : "action",
      position: storedNodeById.get(canvasId)?.position ?? { x: 225, y: index * 150 + 100 },
      data: {
        ...storedData,
        ...definedServerFields,
        actionType: action.actionType,
        label: (storedData.label as string) ?? NODE_LABELS[action.actionType],
      },
    });

    const branchEntries = hasStoredBranches
      ? Object.entries(action.branches ?? {})
      : deriveLinearBranch(action, actions[index + 1]);

    branchEntries.forEach(([edgeKey, targetServerId]) => {
      const targetCanvasId = canvasIdByServerId.get(targetServerId) ?? targetServerId;
      edges.push({
        id: `edge-${canvasId}-${edgeKey}`,
        source: canvasId,
        sourceHandle: edgeKey,
        target: targetCanvasId,
        label: edgeLabelFor(edgeKey),
        markerEnd: { type: MarkerType.ArrowClosed },
      });
    });
  });

  const rootCanvasId = actions[0].clientId ?? actions[0].id;
  if (rootCanvasId) {
    edges.push({
      id: `edge-start-${rootCanvasId}`,
      source: "start",
      target: rootCanvasId,
      markerEnd: { type: MarkerType.ArrowClosed },
    });
  }

  return { nodes, edges };
}

function deriveLinearBranch(action: CallFlowAction, follower?: CallFlowAction): Array<[string, string]> {
  const edgeKey = NODE_EDGES[action.actionType]?.[0];
  const followerId = follower?.id;
  if (!edgeKey || !followerId) {
    return [];
  }
  return [[edgeKey, followerId]];
}

function buildActionMetadata(data: CallFlowNodeData): Record<string, unknown> {
  const metadata: Record<string, unknown> = {};

  if (data.actionType === "RING_GROUP" || data.actionType === "ROUND_ROBIN") {
    const ringMembers = Array.isArray(data.ringMembers) ? data.ringMembers.map((id: unknown) => String(id)) : [];
    if (data.ringTeamId) {
      metadata.ringTeamId = data.ringTeamId;
    }
    if (ringMembers.length > 0) {
      metadata.ringMembers = ringMembers;
    }
    if (data.ringStrategy) {
      metadata.ringStrategy = data.ringStrategy;
    }
    if (typeof data.ringSeconds === "number") {
      metadata.ringSeconds = data.ringSeconds;
    }
  }

  if (data.actionType === "CALL_NUMBER") {
    if (data.whisper?.mode === "SAY" && data.whisper.text) {
      metadata.whisper = { mode: "SAY", text: data.whisper.text };
    }
    if (data.whisper?.mode === "PLAY" && data.whisper.audioClipId) {
      metadata.whisper = {
        mode: "PLAY",
        audioClipId: data.whisper.audioClipId,
        audioClipName: data.whisper.audioClipName,
      };
    }
    if (data.forwardCallerIdMode) {
      metadata.forwardCallerIdMode = data.forwardCallerIdMode;
    }
    metadata.forwardRecord = data.forwardRecord === true;
  }

  if (data.actionType === "ROUND_ROBIN") {
    metadata.ringRounds = Number(data.ringRounds) || 1;
    metadata.ringStickyAgent = data.ringStickyAgent !== false;
  }

  if (data.actionType === "VOICEMAIL") {
    metadata.voicemailMaxLengthSeconds = Number(data.voicemailMaxLengthSeconds) || 300;
    metadata.voicemailTranscribe = data.voicemailTranscribe === true;
    metadata.voicemailGreetingSource = data.voicemailGreetingSource || "STEP";
  }

  if (data.actionType === "BUSINESS_HOURS" && data.businessHours) {
    metadata.businessHours = {
      days: data.businessHours.days ?? {},
      holidays: (data.businessHours.holidays ?? []).filter((holiday: string) => holiday.length > 0),
    };
  }

  if (data.actionType === "MENU") {
    const menuKeys = Array.isArray(data.menuKeys) ? data.menuKeys.map((key: unknown) => String(key)) : [];
    if (menuKeys.length > 0) {
      metadata.menuKeys = menuKeys;
    }
    if (typeof data.menuRepeatCount === "number") {
      metadata.menuRepeatCount = data.menuRepeatCount;
    }
    if (data.menuReturnKey) {
      metadata.menuReturnKey = String(data.menuReturnKey);
    }
    if (data.menuKeyLabels && Object.keys(data.menuKeyLabels).length > 0) {
      metadata.menuKeyLabels = data.menuKeyLabels;
    }
    if (data.invalidPrompt?.mode === "SAY" && data.invalidPrompt.text) {
      metadata.invalidPrompt = { mode: "SAY", text: data.invalidPrompt.text };
    }
    if (data.invalidPrompt?.mode === "PLAY" && data.invalidPrompt.audioClipId) {
      metadata.invalidPrompt = {
        mode: "PLAY",
        audioClipId: data.invalidPrompt.audioClipId,
        audioClipName: data.invalidPrompt.audioClipName,
      };
    }
  }

  if (data.prompt?.mode === "SAY" && data.prompt.text) {
    metadata.prompt = { mode: "SAY", text: data.prompt.text };
  }
  if (data.prompt?.mode === "PLAY" && data.prompt.audioClipId) {
    metadata.prompt = {
      mode: "PLAY",
      audioClipId: data.prompt.audioClipId,
      audioClipName: data.prompt.audioClipName,
    };
  }

  if (data.actionType === "SAY_MESSAGE" || data.actionType === "DNC_OPT_OUT") {
    if (data.voice) {
      metadata.voice = data.voice;
    }
    if (data.language) {
      metadata.language = data.language;
    }
    if (typeof data.loop === "number" && data.loop > 0) {
      metadata.loop = data.loop;
    }
    if (typeof data.pauseLength === "number" && data.pauseLength > 0) {
      metadata.pauseLength = data.pauseLength;
    }
  }

  if (data.actionType === "CALL_NUMBER") {
    if (data.voice) {
      metadata.voice = data.voice;
    }
    if (data.language) {
      metadata.language = data.language;
    }
    if (typeof data.loop === "number" && data.loop > 0) {
      metadata.loop = data.loop;
    }
    if (typeof data.pauseLength === "number" && data.pauseLength > 0) {
      metadata.pauseLength = data.pauseLength;
    }
  }

  if (data.actionType === "AI_AGENT") {
    // aiAgentId is required for the backend to route the call to the agent; without it the
    // flow renders "AI agent is not configured for this action."
    if (data.aiAgentId) {
      metadata.aiAgentId = data.aiAgentId;
    }
    if (data.callScope) {
      metadata.callScope = data.callScope;
    }
    if (data.transferMembershipId) {
      metadata.transferMembershipId = data.transferMembershipId;
    }
    if (data.transferPhoneNumberId) {
      metadata.transferPhoneNumberId = data.transferPhoneNumberId;
    }
    if (data.transferNumber) {
      metadata.transferNumber = data.transferNumber;
    }
  }

  return metadata;
}
